import React from "react";
import { Box, Button, Card, Divider, IconButton, Stack, Tooltip, Typography, alpha } from "@mui/material";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import StepUpSpinner from "../../../../Components/Loader/StepUpSpinner";

const NAVY = "#1b3a6b";
const INK = "#0f172a";
const MUTED = "#64748b";
const HAIRLINE = "#e2e8f0";

type Props = {
  refreshedAt: Date | null;
  loading: boolean;
  exporting: null | "csv" | "pdf";
  onRefresh: () => void;
  onCsv: () => void;
  onPdf: () => void;
};

// Human "5:42 PM" — no seconds, no leading zeros.
function formatClock(d: Date): string {
  const h24 = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const am = h24 < 12;
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}:${m} ${am ? "AM" : "PM"}`;
}

export default function PremiumHeader({
  refreshedAt, loading, exporting, onRefresh, onCsv, onPdf,
}: Props) {
  return (
    <Card
      variant="outlined"
      sx={{ p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 3, borderColor: HAIRLINE }}
      className="stur-header"
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        {/* Left — icon block + title + subtitle + last-refreshed */}
        <Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 44, height: 44, borderRadius: "12px",
              bgcolor: alpha(NAVY, 0.09), color: NAVY,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <SchoolOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: INK, lineHeight: 1.2 }}>
              Student Timetable Report
            </Typography>
            <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.25 }}>
              Weekly class schedule by student — review, filter, export and print.
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.5, opacity: 0.75 }} className="stur-hide-print">
              {refreshedAt ? `Last refreshed ${formatClock(refreshedAt)}` : "Never refreshed"}
            </Typography>
          </Box>
        </Stack>

        {/* Right — Refresh (icon) + Export group */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" className="stur-hide-print">
          <Tooltip title={loading ? "Refreshing…" : "Refresh"}>
            <span>
              <IconButton
                onClick={onRefresh}
                disabled={loading}
                size="small"
                aria-label="Refresh"
                sx={{
                  border: `1px solid ${HAIRLINE}`,
                  borderRadius: 2,
                  color: INK,
                  "&:hover": { borderColor: NAVY, bgcolor: alpha(NAVY, 0.04) },
                }}
              >
                <RefreshOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: HAIRLINE }} />

          {/* Export group — CSV / PDF / Print sit together for clarity */}
          <Stack direction="row" spacing={0.75}>
            <Button
              variant="outlined"
              size="small"
              startIcon={exporting === "csv" ? <StepUpSpinner size="small" /> : <FileDownloadOutlinedIcon />}
              onClick={onCsv}
              disabled={loading || !!exporting}
              sx={{
                textTransform: "none", borderRadius: 2, fontWeight: 600,
                borderColor: HAIRLINE, color: INK,
                "&:hover": { borderColor: NAVY, bgcolor: alpha(NAVY, 0.04) },
              }}
            >
              {exporting === "csv" ? "Preparing…" : "CSV"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={exporting === "pdf" ? <StepUpSpinner size="small" /> : <PictureAsPdfOutlinedIcon />}
              onClick={onPdf}
              disabled={loading || !!exporting}
              sx={{
                textTransform: "none", borderRadius: 2, fontWeight: 600,
                borderColor: HAIRLINE, color: INK,
                "&:hover": { borderColor: NAVY, bgcolor: alpha(NAVY, 0.04) },
              }}
            >
              {exporting === "pdf" ? "Preparing…" : "PDF"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintOutlinedIcon />}
              onClick={() => window.print()}
              disabled={loading}
              sx={{
                textTransform: "none", borderRadius: 2, fontWeight: 600,
                borderColor: HAIRLINE, color: INK,
                "&:hover": { borderColor: NAVY, bgcolor: alpha(NAVY, 0.04) },
              }}
            >
              Print
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}
